import { getToken } from 'firebase/app-check';
import { auth, getAppCheckInstance } from './firebase';
import { TRACE_HEADER, getTraceId } from './logger';
import { readActiveOrgId } from './active-org';

/**
 * Build the headers any authenticated `/api/*` request needs:
 *   - X-Request-Id for client/server log correlation
 *   - Authorization: Bearer <id-token> when a user is signed in
 *   - X-Firebase-AppCheck: attested-origin token when App Check is wired
 *
 * Awaits `auth.authStateReady()` so requests firing on first paint don't
 * race the Firebase Auth SDK rehydrating from IndexedDB and ship without
 * a Bearer token (would 401 the signed-in user).
 *
 * App Check is best-effort: failures here never block the request. The
 * server's guard logs a warn when the header is missing and only rejects
 * once `APP_CHECK_ENFORCE=true` is set on the Functions runtime.
 */
export async function authHeaders(): Promise<Record<string, string>> {
  await auth.authStateReady();
  const headers: Record<string, string> = {
    [TRACE_HEADER]: getTraceId(),
  };
  const user = auth.currentUser;
  if (user) {
    headers.Authorization = `Bearer ${await user.getIdToken()}`;
  }
  // Multi-tenant: every authed request carries the active org id so the
  // server can scope reads/writes. Persisted in localStorage so SSE +
  // non-React call sites share the same value as the React tree.
  const activeOrgId = readActiveOrgId();
  if (activeOrgId) {
    headers['X-Org-Id'] = activeOrgId;
  }
  const appCheck = getAppCheckInstance();
  if (appCheck) {
    try {
      const { token } = await getToken(appCheck, /* forceRefresh */ false);
      if (token) headers['X-Firebase-AppCheck'] = token;
    } catch {
      // App Check couldn't produce a token (offline, reCAPTCHA hiccup,
      // network transient). Ship without — server logs, optionally
      // enforces. A retried request usually succeeds once the SDK
      // completes a fresh exchange round-trip.
    }
  }
  return headers;
}
